import { useCallback, useState } from "react";
import { Box, Stack, Typography, styled, type SxProps, type Theme } from "~/common/mui";
import { CarouselSelectControl, ControlRenderer, type ConfiguratorControlChange } from "../controls";
import type { CarouselColumnsSubstepViewModel } from "./substepViewModel";

export interface CarouselColumnsSubstepProps {
  viewModel: CarouselColumnsSubstepViewModel;
  onControlChange: (change: ConfiguratorControlChange) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const Columns = styled(Box, {
  shouldForwardProp: (prop) => prop !== "columnCount",
})<{ columnCount: number }>(({ columnCount, theme }) => ({
  width: "100%",
  maxWidth: columnCount * 416 + Math.max(columnCount - 1, 0) * 16,
  marginInline: "auto",
  display: "grid",
  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
  gap: theme.spacing(2),
  alignItems: "start",
  justifyItems: "center",
}));

export const CarouselColumnsSubstep = ({ viewModel, onControlChange, className, sx }: CarouselColumnsSubstepProps) => {
  const [selectedCardHeights, setSelectedCardHeights] = useState<Record<number, number>>({});
  const synchronizeSelectedCardHeight = viewModel.columns.length > 1;
  const synchronizedCardHeight = synchronizeSelectedCardHeight
    ? Math.max(...viewModel.columns.map(({ carousel }) => selectedCardHeights[carousel.controlIndex] ?? 0)) || undefined
    : undefined;
  const reportSelectedCardHeight = useCallback((controlIndex: number, height: number) => {
    setSelectedCardHeights((currentHeights) =>
      currentHeights[controlIndex] === height ? currentHeights : { ...currentHeights, [controlIndex]: height },
    );
  }, []);

  return (
    <Stack className={className} sx={sx} gap={2}>
      {viewModel.title ? <Typography variant="bodyLarge">{viewModel.title}</Typography> : null}
      {viewModel.description ? (
        <Typography variant="bodyMedium" color="textSecondary">
          {viewModel.description}
        </Typography>
      ) : null}
      <Columns columnCount={viewModel.columns.length}>
        {viewModel.columns.map((column) => {
          const commitCarousel = (value: string) =>
            onControlChange({ controlIndex: column.carousel.controlIndex, value });

          return (
            <CarouselSelectControl
              key={column.carousel.controlIndex}
              {...column.carousel}
              onChange={commitCarousel}
              synchronizedCardHeight={synchronizedCardHeight}
              onSelectedCardHeightChange={
                synchronizeSelectedCardHeight
                  ? (height) => reportSelectedCardHeight(column.carousel.controlIndex, height)
                  : undefined
              }
            >
              {column.details.map((detail) => (
                <ControlRenderer
                  key={detail.controlIndex}
                  viewModel={detail}
                  appearance="carouselDetail"
                  onChange={onControlChange}
                />
              ))}
            </CarouselSelectControl>
          );
        })}
      </Columns>
    </Stack>
  );
};
